import React, { useState, useEffect } from "react";
import { Spin, Alert, Empty, Card } from "antd";
import { getAppointments, Appointment } from "../../../api/appointment.api";
import { useParams } from "react-router-dom";
import { Paciente } from "../../../api/paciente.api";
import { CheckCircleOutlined, BellOutlined } from "@ant-design/icons";

interface AppointmentListProps {
  paciente?: Paciente;
}

const formatDate = (dateString: string): string => {
  try {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    return new Date(dateString).toLocaleString("es-SV", options);
  } catch (e) {
    console.error("Error formatting date:", dateString, e);
    return dateString;
  }
};

const AppointmentList: React.FC<AppointmentListProps> = ({ paciente }) => {
  const { id } = useParams<{ id: string }>();
  const pacienteId = paciente
    ? paciente.pacienteId
    : id
    ? parseInt(id, 10)
    : null;

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (pacienteId === null) {
      setError("ID de paciente inválido.");
      setLoading(false);
      return;
    }
    const fetchAppointments = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getAppointments();
        const sortedData = data.sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        const filteredData = sortedData.filter(
          (appointment) => appointment.patientId === pacienteId
        );
        setAppointments(filteredData);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Ocurrió un error desconocido al cargar las citas.";
        setError(errorMessage);
        console.error("Failed to fetch appointments:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAppointments();
  }, [pacienteId]);

  // Construct the full patient name once using the paciente prop
  const patientFullNameFromProp = paciente
    ? [
        paciente.nombre1,
        paciente.nombre2,
        paciente.apellido1,
        paciente.apellido2,
      ]
        .filter(Boolean)
        .join(" ")
    : "Nombre no disponible"; // Fallback if paciente prop is somehow undefined

  if (loading) {
    return (
      <div className="appointments-container">
        <Card
          bordered={false}
          style={{
            borderRadius: "8px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
              padding: "1rem",
            }}
          >
            <Spin tip="Cargando historial de citas..." size="large" />
          </div>
        </Card>
      </div>
    );
  }
  if (error) {
    return (
      <div className="appointments-container">
        <Card
          bordered={false}
          style={{
            borderRadius: "8px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
          }}
        >
          <Alert message="Error" description={error} type="error" showIcon />
        </Card>
      </div>
    );
  }
  if (appointments.length === 0) {
    return (
      <div className="appointments-container">
        <Card
          bordered={false}
          style={{
            borderRadius: "8px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
          }}
        >
          <Empty description="No se encontraron citas para este paciente." />
        </Card>
      </div>
    );
  }

  // --- VISTA DESKTOP (tabla) ---
  const getStatusProps = (status: string) => {
    const s = status.toLowerCase();
    if (s === "pendiente" || s === "pending") {
      return {
        color: "#faad14",
        bg: "#fffbe6",
        label: "Pendiente",
        icon: (
          <CheckCircleOutlined style={{ color: "#faad14" }} />
        ),
      };
    } else if (
      s === "cancelada" ||
      s === "cancelado" ||
      s === "cancelled"
    ) {
      return {
        color: "#ff4d4f",
        bg: "#fff1f0",
        label: "Cancelada",
        icon: (
          <CheckCircleOutlined style={{ color: "#ff4d4f" }} />
        ),
      };
    } else if (
      s === "completada" ||
      s === "completado" ||
      s === "completed"
    ) {
      return {
        color: "#1890ff",
        bg: "#e6f7ff",
        label: "Completada",
        icon: (
          <CheckCircleOutlined style={{ color: "#1890ff" }} />
        ),
      };
    } else if (
      s === "confirmada" ||
      s === "confirmado" ||
      s === "confirmed"
    ) {
      return {
        color: "#52c41a",
        bg: "#f6ffed",
        label: "Confirmada",
        icon: (
          <CheckCircleOutlined style={{ color: "#52c41a" }} />
        ),
      };
    }
    return {
      color: "#8c8c8c",
      bg: "#fafafa",
      label: status,
      icon: (
        <CheckCircleOutlined style={{ color: "#8c8c8c" }} />
      ),
    };
  };

  return (
    <div className="appointments-container">
      {/* Vista Desktop: Tabla */}
      <div className="desktop-only">
        <Card bordered={false}>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: 900,
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "#fafafa",
                    borderBottom: "2px solid #f0f0f0",
                  }}
                >
                  <th
                    style={{
                      textAlign: "left",
                      padding: "10px 8px",
                      fontWeight: 600,
                    }}
                  >
                    Fecha y Hora
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "10px 8px",
                      fontWeight: 600,
                    }}
                  >
                    Doctor
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "10px 8px",
                      fontWeight: 600,
                    }}
                  >
                    Estado
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "10px 8px",
                      fontWeight: 600,
                    }}
                  >
                    Notificaciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {appointments.map((appointment) => {
                  const fullOdontoName =
                    appointment.odontoName ||
                    (appointment.odonto
                      ? [appointment.odonto.nombres, appointment.odonto.apellidos]
                          .filter(Boolean)
                          .join(" ")
                      : "Nombre no disponible");
                  const statusProps = getStatusProps(appointment.status);
                  return (
                    <tr
                      key={appointment.id}
                      style={{
                        borderBottom: "1px solid #f0f0f0",
                        background: statusProps.bg,
                      }}
                    >
                      <td
                        style={{
                          padding: "12px 8px",
                          verticalAlign: "top",
                          minWidth: 220,
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 500,
                            color: "#262626",
                            fontSize: 15,
                          }}
                        >
                          {formatDate(appointment.date)}
                        </div>
                        <div
                          style={{ color: "#8c8c8c", fontSize: 13 }}
                        >{`Creada: ${formatDate(appointment.createdAt)}`}</div>
                        <div
                          style={{ color: "#8c8c8c", fontSize: 13 }}
                        >{`Tel: ${appointment.patient?.telefono || "-"}`}</div>
                      </td>
                      <td
                        style={{
                          padding: "12px 8px",
                          verticalAlign: "top",
                          minWidth: 180,
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 500,
                            color: "#262626",
                            fontSize: 15,
                          }}
                        >
                          {fullOdontoName}
                        </div>
                        <div
                          style={{ color: "#8c8c8c", fontSize: 13 }}
                        >{`Cubículo: ${appointment.cubicle || "Sin cubículo"}`}</div>
                        <div
                          style={{ color: "#8c8c8c", fontSize: 13 }}
                        >{`Especialidad: ${appointment.specialty || "Sin especialidad"}`}</div>
                      </td>
                      <td
                        style={{
                          padding: "12px 8px",
                          verticalAlign: "top",
                          minWidth: 140,
                        }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            background: statusProps.bg,
                            color: statusProps.color,
                            border: `1px solid ${statusProps.color}`,
                            borderRadius: 6,
                            padding: "2px 10px",
                            fontWeight: 500,
                            fontSize: 14,
                            gap: 4,
                          }}
                        >
                          {statusProps.icon} {statusProps.label}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: "12px 8px",
                          verticalAlign: "top",
                          minWidth: 220,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            color: appointment.reminderSent ? "#52c41a" : "#8c8c8c",
                            fontSize: 15,
                          }}
                        >
                          <BellOutlined />{" "}
                          {appointment.reminderSent
                            ? "Recordatorio enviado"
                            : "Recordatorio pendiente"}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            color: appointment.confirmedByPatient
                              ? "#52c41a"
                              : "#faad14",
                            fontSize: 15,
                            marginTop: 2,
                          }}
                        >
                          <CheckCircleOutlined />{" "}
                          {appointment.confirmedByPatient
                            ? "Confirmado por paciente"
                            : "Pendiente de confirmación"}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Vista Mobile: Cards */}
      <div className="mobile-only">
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {appointments.map((appointment) => {
            const fullPatientName = patientFullNameFromProp;
            const fullOdontoName =
              appointment.odontoName ||
              (appointment.odonto
                ? [appointment.odonto.nombres, appointment.odonto.apellidos]
                    .filter(Boolean)
                    .join(" ")
                : "Nombre no disponible");
            let statusColor = "#52c41a";
            let statusBg = "#f6ffed";
            let cardBg = "#f9fffb";
            const status = appointment.status.toLowerCase();
            if (status === "pendiente" || status === "pending") {
              statusColor = "#faad14";
              statusBg = "#fffbe6";
              cardBg = "#fffbe6";
            } else if (status === "cancelada" || status === "cancelado" || status === "cancelled") {
              statusColor = "#ff4d4f";
              statusBg = "#fff1f0";
              cardBg = "#fff1f0";
            } else if (status === "completada" || status === "completado" || status === "completed") {
              statusColor = "#1890ff";
              statusBg = "#e6f7ff";
              cardBg = "#e6f7ff";
            } else if (status === "confirmada" || status === "confirmado" || status === "confirmed") {
              statusColor = "#52c41a";
              statusBg = "#f6ffed";
              cardBg = "#f6ffed";
            }
            return (
              <Card
                key={appointment.id}
                bordered={false}
                style={{
                  borderRadius: 12,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                  marginBottom: 12,
                  background: cardBg,
                  borderLeft: `4px solid ${statusColor}`,
                  padding: 0,
                }}
                bodyStyle={{ padding: 18 }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        color: "#262626",
                        fontSize: 16,
                      }}
                    >
                      {formatDate(appointment.date)}
                    </span>
                    <span
                      style={{
                        background: statusBg,
                        color: statusColor,
                        border: `1px solid ${statusColor}`,
                        borderRadius: 6,
                        padding: "2px 10px",
                        fontWeight: 500,
                        fontSize: 14,
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <CheckCircleOutlined style={{ color: statusColor }} />{" "}
                      {appointment.status}
                    </span>
                  </div>
                  <div style={{ color: "#8c8c8c", fontSize: 13 }}>
                    Tel: {appointment.patient?.telefono || "-"}
                  </div>
                  <div style={{ fontWeight: 500, fontSize: 15, color: "#262626" }}>
                    {fullPatientName}
                  </div>
                  <div style={{ color: "#8c8c8c", fontSize: 13 }}>
                    Odontólogo: {fullOdontoName}
                  </div>
                  <div style={{ color: "#8c8c8c", fontSize: 13 }}>
                    {`Cubículo: ${appointment.cubicle || "Sin cubículo"}`}
                  </div>
                  <div style={{ color: "#8c8c8c", fontSize: 13 }}>
                    {`Especialidad: ${appointment.specialty || "Sin especialidad"}`}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                    <span
                      style={{
                        color: appointment.reminderSent ? "#52c41a" : "#8c8c8c",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 15,
                      }}
                    >
                      <BellOutlined /> Recordatorio enviado
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span
                      style={{
                        color: appointment.confirmedByPatient ? "#52c41a" : "#8c8c8c",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 15,
                      }}
                    >
                      <CheckCircleOutlined /> Confirmado por paciente
                    </span>
                  </div>
                  <div style={{ color: "#8c8c8c", fontSize: 13, marginTop: 2 }}>
                    Registrada el: {formatDate(appointment.createdAt)}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AppointmentList;
